import { useCallback, useEffect, useRef, useState } from "react";

/** Unsent text by key, so a box that unmounts (going back, closing a card) gets its words back. */
const drafts = new Map<string, string>();

/** `useState` for a message box whose text survives leaving the view; sending clears it. */
export function useDraft(key: string | null): [string, (next: string | ((prev: string) => string)) => void] {
  const [text, setText] = useState(() => (key ? (drafts.get(key) ?? "") : ""));
  const current = useRef({ key, text });
  current.current = { key, text };

  // A different key is a different box: show what that one held.
  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    setText(key ? (drafts.get(key) ?? "") : "");
  }, [key]);

  const set = useCallback((next: string | ((prev: string) => string)) => {
    setText((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      const k = current.current.key;
      if (k) {
        if (value) drafts.set(k, value);
        else drafts.delete(k);
      }
      return value;
    });
  }, []);

  return [text, set];
}
